import { type DefaultContext, mutationPromise } from '@egw/clients'
import { createMutationHook, createQueryHook } from '../../../../hooks'
import {
  type EnrollmentMultiResendReturnVerifyPhoneSubmitInput,
  type EnrollmentMultiReturnVerifyPhoneData,
  type EnrollmentMultiReturnVerifyPhoneSubmitInput,
  type EnrollmentMultiReturnVerifyPhoneSubmitOutput,
  type EnrollmentMultiVerifyPhoneContinueApplicationOutput,
} from '../../../../models/enrollment/multi/return-verify-phone'

/**
 * Get Enrollment Multi ReturnVerifyPhone data
 * @param input - ReentryInput
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiReturnVerifyPhoneData>
 * @example const response = await getEnrollmentMultiReturnVerifyPhoneData()
 */
export const getEnrollmentMultiReturnVerifyPhoneData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiReturnVerifyPhoneData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiReturnVerifyPhoneData',
      query: `mutation getEnrollmentMultiReturnVerifyPhoneData($input: ReentryInput!) {
        enterApplication(input: $input) {
          phone {
            countryPhoneCode
            phoneNumber
          }
          nextStep
          error
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { enterApplication: EnrollmentMultiReturnVerifyPhoneData }
  return response.enterApplication
}

export const useGetEnrollmentMultiReturnVerifyPhoneData = createQueryHook<
  undefined,
  EnrollmentMultiReturnVerifyPhoneData
>(getEnrollmentMultiReturnVerifyPhoneData)

/**
 * Submit Enrollment Multi ReturnVerifyPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiReturnVerifyPhoneSubmitOutput>
 * @example const response = await submitEnrollmentMultiReturnVerifyPhoneData();
 */
export const submitEnrollmentMultiReturnVerifyPhoneData = async (
  enrollmentReturnVerifyPhoneSubmitInput: EnrollmentMultiReturnVerifyPhoneSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiReturnVerifyPhoneSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiReturnVerifyPhoneData',
      query: `mutation submitEnrollmentMultiReturnVerifyPhoneData($input: ReentryInput!) {
        enterApplication(input: $input) {
          phone {
            countryPhoneCode
            phoneNumber
          }
          enrollmentType
          nextStep
          error
        }
      }`,
    },
    {
      input: enrollmentReturnVerifyPhoneSubmitInput,
    },
    context,
  )) as { enterApplication: EnrollmentMultiReturnVerifyPhoneSubmitOutput }
  return response.enterApplication
}

export const useSubmitEnrollmentMultiReturnVerifyPhoneData = createMutationHook<
  EnrollmentMultiReturnVerifyPhoneSubmitInput,
  EnrollmentMultiReturnVerifyPhoneSubmitOutput
>(submitEnrollmentMultiReturnVerifyPhoneData)

/**
 * Submit Enrollment Multi ReturnVerifyPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiReturnVerifyPhoneSubmitOutput>
 * @example const response = await submitEnrollmentMultiReturnVerifyPhoneData();
 */
export const submitEnrollmentMultiResendReturnVerifyPhoneData = async (
  enrollmentResendReturnVerifyPhoneSubmitInput: EnrollmentMultiResendReturnVerifyPhoneSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiReturnVerifyPhoneSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiResendReturnVerifyPhoneData',
      query: `mutation submitEnrollmentMultiResendReturnVerifyPhoneData($input: ReentryInput!) {
        enterApplication(input: $input) {
          phone {
            countryPhoneCode
            phoneNumber
          }
          nextStep
          error
        }
      }`,
    },
    {
      input: enrollmentResendReturnVerifyPhoneSubmitInput,
    },
    context,
  )) as { enterApplication: EnrollmentMultiReturnVerifyPhoneSubmitOutput }
  return response.enterApplication
}

export const useSubmitEnrollmentMultiResendReturnVerifyPhoneData = createMutationHook<
  EnrollmentMultiResendReturnVerifyPhoneSubmitInput,
  EnrollmentMultiReturnVerifyPhoneSubmitOutput
>(submitEnrollmentMultiResendReturnVerifyPhoneData)

/**
 * Submit Enrollment  VerifyPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentVerifyPhoneSubmitOutput>
 * @example const response = await submitEnrollmentVerifyPhoneData();
 */
export const submitEnrollmentMultiReturnVerifyPhoneOtpData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiVerifyPhoneContinueApplicationOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentVerifyPhoneData',
      query: `mutation submitEnrollmentVerifyPhoneData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiVerifyPhoneContinueApplicationOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiReturnVerifyPhoneOtpData = createMutationHook<
  undefined,
  EnrollmentMultiVerifyPhoneContinueApplicationOutput
>(submitEnrollmentMultiReturnVerifyPhoneOtpData)
